import React, {
  useState, useContext, useMemo, useRef, useEffect,
} from 'react';
import { LPSGroundProps } from 'types/webPlayer';
import { CursorContext } from '../track/TrackChartContainer';
import { useQueryCached } from '../utils/graphql';
import { GroundRes, GroundVars, GROUND_DETAILS } from '../../query/grounds';
import { CACHE_AND_NETWORK } from '../../lib/cache';
import Error from '../layout/Error';
import GroundField from './GroundField';
import { getPlayersLines, getPlayersPointsLPS, getSizesFromRef } from '../utils/groundGbf';
import { GroundType } from '../../models/ground';
import { useDebouncedResize } from '../../lib/dom';
import { rotateRectangle } from '../track/ground/calc';
import { FULLSCREEN_SCALE_MULTIPLIER } from './utils';
import VectorGroundField from './VectorGroundField';

function getScale(
  width: number,
  height: number,
  winWidth: number,
  winHeight: number,
) {
  const scaleX = winWidth / width;
  const scaleY = winHeight / height;
  return Math.min(scaleX, scaleY);
}

const GroundLPS = (props: LPSGroundProps) => {
  const {
    activePlayer,
    hoverPlayer,
    defaultPlayersColor,
    enabledPlayers,
    fullscreenMode,
    groundId,
    infoPlayers,
    jerseyOrNumber,
    onPlayerClick,
    onPlayerEnter,
    onPlayerLeave,
    playerLinks,
    playersColors,
    playersDetails,
    removeLink,
    series,
    showGroundName,
    showVertices,
    teamColors,
    trailsEnabled,
    userUnits,
  } = props;

  const parentRef = useRef<HTMLDivElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [lpsGroundData, setLpsGroundData] = useState<GroundType | undefined>(undefined);
  const [scale, setScale] = useState(1);
  const [fullscreenScaleMultiplier, setFullscreenScaleMultiplier] = useState(fullscreenMode ? FULLSCREEN_SCALE_MULTIPLIER : 1);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [angle, setAngle] = useState(0);
  const [center, setCenter] = useState({ x: 0, y: 0 });
  const [isCW, setIsCW] = useState(true);
  const { cursor } = useContext(CursorContext);
  const [vertex, setVertex] = useState({
    vertexA: {
      x: 0,
      y: 0,
    },
    vertexB: {
      x: 0,
      y: 0,
    },
    vertexC: {
      x: 0,
      y: 0,
    },
    vertexD: {
      x: 0,
      y: 0,
    },
  });

  const [windowSize, setWindowSize] = useState({
    WINDOW_WIDTH: 0,
    WINDOW_HEIGHT: 0,
  });

  useEffect(() => {
    setWindowSize(getSizesFromRef(parentRef));
  }, []);

  useDebouncedResize(() => {
    setWindowSize(getSizesFromRef(parentRef));
    setFullscreenChanges((prevState) => prevState + 1);
  }, 200);

  const [fullscreenChanges, setFullscreenChanges] = useState(1);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setWindowSize(getSizesFromRef(parentRef));
      setFullscreenChanges((prevState) => prevState + 1);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const {
    WINDOW_WIDTH,
  } = windowSize;

  const WINDOW_HEIGHT = Math.floor(WINDOW_WIDTH * 9 / 16);

  const {
    data: groundDetailsData,
    error: errorGround,
  } = useQueryCached<GroundRes, GroundVars>(GROUND_DETAILS, {
    variables: {
      id: groundId,
    },
    skip: groundId == null,
    ...CACHE_AND_NETWORK,
    onCompleted: (response) => {
      /*// sovrascrivo i dati locali nei dettagli del ground da passare al Pitch
      setLpsGroundData(response.res);
      // ruoto il ground in base al suo centro
      const adjustedData = rotateRectangle({
        A: { x: response.res.vertexAX.value, y: response.res.vertexAY.value },
        B: { x: response.res.vertexBX.value, y: response.res.vertexBY.value },
        C: { x: response.res.vertexCX.value, y: response.res.vertexCY.value },
        D: { x: response.res.vertexDX.value, y: response.res.vertexDY.value },
      });

      // calcolo le dimensioni del campo (dai dati)
      const width = Math.abs(adjustedData.vertices.C.x - adjustedData.vertices.A.x);
      const height = Math.abs(adjustedData.vertices.C.y - adjustedData.vertices.A.y);

      // calcolo il valore della scala per adattare il campo alla canvas
      const scaleValue = getScale(
        width + 10,
        height + 10,
        WINDOW_WIDTH,
        WINDOW_HEIGHT,
      );

      setVertex({
        vertexA: {
          x: adjustedData.vertices.A.x * scaleValue,
          y: -adjustedData.vertices.A.y * scaleValue,
        },
        vertexC: {
          x: adjustedData.vertices.C.x * scaleValue,
          y: -adjustedData.vertices.C.y * scaleValue,
        },
      });

      setDims({ width: width * scaleValue, height: height * scaleValue });
      setCenter({
        x: adjustedData.rotationInfo.centerX * scaleValue,
        y: -adjustedData.rotationInfo.centerY * scaleValue,
      });
      setAngle(adjustedData.rotationInfo.angle * (180 / Math.PI));
      setIsCW(adjustedData.rotationInfo.isClockwise);
      setScale(scaleValue);*/
    },
  });



  useEffect(() => {
    if (!groundDetailsData) return;

    // sovrascrivo i dati locali nei dettagli del ground da passare al Pitch
    setLpsGroundData(groundDetailsData.res);
    // ruoto il ground in base al suo centro
    const adjustedData = rotateRectangle({
      A: { x: groundDetailsData.res.vertexAX.value, y: groundDetailsData.res.vertexAY.value },
      B: { x: groundDetailsData.res.vertexBX.value, y: groundDetailsData.res.vertexBY.value },
      C: { x: groundDetailsData.res.vertexCX.value, y: groundDetailsData.res.vertexCY.value },
      D: { x: groundDetailsData.res.vertexDX.value, y: groundDetailsData.res.vertexDY.value },
    });

    // calcolo le dimensioni del campo (dai dati)
    const width = Math.abs(adjustedData.vertices.C.x - adjustedData.vertices.A.x);
    const height = Math.abs(adjustedData.vertices.C.y - adjustedData.vertices.A.y);

    // calcolo il valore della scala per adattare il campo alla canvas
    const scaleValue = getScale(
      width + 10,
      height + 10,
      WINDOW_WIDTH,
      WINDOW_HEIGHT,
    );

    setVertex({
      vertexA: {
        x: adjustedData.vertices.A.x * scaleValue,
        y: -adjustedData.vertices.A.y * scaleValue,
      },
      vertexB: {
        x: adjustedData.vertices.B.x * scaleValue,
        y: -adjustedData.vertices.B.y * scaleValue,
      },
      vertexC: {
        x: adjustedData.vertices.C.x * scaleValue,
        y: -adjustedData.vertices.C.y * scaleValue,
      },
      vertexD: {
        x: adjustedData.vertices.D.x * scaleValue,
        y: -adjustedData.vertices.D.y * scaleValue,
      },
    });

    setDims({ width: width * scaleValue, height: height * scaleValue });
    setCenter({
      x: adjustedData.rotationInfo.centerX * scaleValue,
      y: -adjustedData.rotationInfo.centerY * scaleValue,
    });
    setAngle(adjustedData.rotationInfo.angle * (180 / Math.PI));
    setIsCW(adjustedData.rotationInfo.isClockwise);
    setScale(scaleValue);
  }, [groundDetailsData, fullscreenChanges]);

  useEffect(() => {
    setFullscreenScaleMultiplier(fullscreenMode ? FULLSCREEN_SCALE_MULTIPLIER : 1);
  }, [fullscreenMode]);

  const effectiveScale = scale * fullscreenScaleMultiplier;

  const scaledWidth = vertex.vertexC.x !== undefined && vertex.vertexC.x !== null
    && vertex.vertexA.x !== undefined && vertex.vertexA.x !== null
    ? Math.abs(vertex.vertexC.x - vertex.vertexA.x)
    : 1;
  const scaledHeight = vertex.vertexA.y !== undefined && vertex.vertexA.y !== null
    && vertex.vertexC.y !== undefined && vertex.vertexC.y !== null
    ? Math.abs(vertex.vertexC.y - vertex.vertexA.y)
    : 1;

  const offX = vertex.vertexA.x - (WINDOW_WIDTH - scaledWidth) / 2;
  const offY = vertex.vertexC.y - (WINDOW_HEIGHT - (isCW ? scaledHeight : -scaledHeight)) / 2;

  const playersPoints = useMemo(() => getPlayersPointsLPS({
    cursor,
    enabledPlayers,
    scale: effectiveScale,
    series,
    angle: angle / (180 / Math.PI),
    center,
    isCW,
  }), [series, cursor, enabledPlayers, scale, fullscreenScaleMultiplier, effectiveScale]);

  const playersLines = useMemo(() => getPlayersLines({
    playersPoints,
    playerLinks,
  }), [playerLinks, playersPoints]);

  if (errorGround) return <Error />;

  return (
    <>
      {showGroundName
        && (
          <div>
            {lpsGroundData?.id}
            {' '}
            -
            {lpsGroundData?.name}
          </div>
        )}

      <VectorGroundField
        WINDOW_HEIGHT={WINDOW_HEIGHT}
        WINDOW_WIDTH={WINDOW_WIDTH}
        activePlayer={activePlayer}
        cursor={cursor}
        dataGround={lpsGroundData}
        defaultPlayersColor={defaultPlayersColor}
        enabledPlayers={enabledPlayers}
        groundGeometry={{
          angle,
          scale: effectiveScale,
          vertex,
          dims,
          center,
          scaledDims: {
            width: scaledWidth,
            height: scaledHeight,
          },
        }}
        hoverPlayer={hoverPlayer}
        infoPlayers={infoPlayers}
        jerseyOrNumber={jerseyOrNumber}
        offX={offX}
        offY={offY}
        onPlayerClick={onPlayerClick}
        onPlayerEnter={onPlayerEnter}
        onPlayerLeave={onPlayerLeave}
        playersColors={playersColors}
        playersDetails={playersDetails}
        playersLines={playersLines}
        playersLinks={playerLinks}
        playersPoints={playersPoints}
        ref={parentRef}
        removeLink={removeLink}
        scale={effectiveScale}
        series={series}
        setZoom={setZoom}
        showVertices={!!showVertices}
        teamColors={teamColors}
        trailsEnabled={trailsEnabled}
        userUnits={userUnits}
        zoom={zoom}
      />
    </>
  );
};

export default GroundLPS;
